import React, { useRef, useState, useEffect } from 'react';
import styled, { createGlobalStyle } from 'styled-components';
import { useTranslation } from '../context/LanguageContext';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  Shield,
  Rocket,
  Brain,
  Users,
  Calendar,
  ListChecks,
  Clock,
  Lightning,
  CheckCircle,
  ArrowsClockwise,
  ChalkboardTeacher,
  Student,
  Buildings,
  Book,
  CheckSquare,
  ChartLine,
  PencilLine,
  ChartBar,
  Gear,
  ChatCircle,
  Calculator,
  ClipboardText,
  GraduationCap,
  MapTrifold,
  UsersThree,
  Files,
  Database,
  ChatText,
  NotePencil,
  Envelope,
  Translate,
  CalendarCheck,
  MegaphoneSimple,
  ShareNetwork,
  ChartLineUp,
  Robot,
  Atom,
  MagicWand,
  Books,
  ArrowRight,
  CaretDown,
  Quotes,
  PresentationChart,
  GameController
} from "@phosphor-icons/react";
import { AnimatePresence, motion, useScroll, useTransform } from 'framer-motion';
import SparkLogo from '../components/SparkLogo';
import ComparisonComponent from '../components/ComparisonSection';
import SEO from '../components/SEO';

const GlobalStyle = createGlobalStyle`
  html {
    -webkit-text-size-adjust: 100%;
  }

  body {
    background: #0A1628;
    margin: 0;
    padding: 0;
    width: 100%;
    overflow-x: hidden;
  }
  
  * {
    box-sizing: border-box;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
  }

  a {
    text-decoration: none;
    color: inherit;
    
    &:hover {
      text-decoration: none;
    }
  }
`;

const Container = styled.div`
  width: 100%;
  min-height: 100vh;
  background: #0A1628;
  overflow: hidden;
`;

const HeroSection = styled.section`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding-top: 80px;
  background: #0A1628;
  color: white;
  text-align: center;
  position: relative;
  overflow: hidden;

  @media (max-width: 768px) {
    padding-top: calc(5vh + 24px);
    padding-bottom: 24px;
    min-height: auto;
  }
`;

const HeroContainer = styled.div`
  width: 1627px;
  max-width: 92%;
  margin: 0 auto;
  padding: 48px 32px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  gap: 24px;
  background-image: url('/images/background.png');
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  border-radius: 24px;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  aspect-ratio: 1808/842;
  overflow: hidden;

  @media (max-width: 768px) {
    padding: 32px 20px;
    aspect-ratio: auto;
    width: calc(100% - 32px);
    max-width: 100%;
    margin: 0 16px;
    border-radius: 16px;
    background: linear-gradient(180deg, rgba(58, 183, 255, 0.1) 0%, rgba(10, 22, 40, 0.9) 100%), url('/images/background.png');
    background-size: cover;
    background-position: center;
    border: 1px solid rgba(58, 183, 255, 0.2);
  }
`;

const HeroContent = styled.div`
  width: 100%;
  max-width: 1600px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 16px;
  margin-top: 8px;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding: 0 16px;

  @media (max-width: 640px) {
    flex-direction: column;
    gap: 12px;
    width: 100%;
    max-width: 320px;
    margin: 8px auto;
  }
`;

const Button = styled(Link)`
  position: relative;
  width: 247px;
  height: 70px;
  display: flex;
  align-items: center;
  justify-content: center;
  text-decoration: none;
  font-family: 'Inter', sans-serif;
  font-weight: 600;
  font-size: 16px;
  line-height: 21px;
  letter-spacing: 0.32px;
  text-transform: none;
  color: #FFFFFF;
  transition: all 0.2s ease;

  @media (max-width: 640px) {
    width: 100%;
    height: 56px;
    font-size: 15px;
    padding: 0 16px;
    min-width: 140px;
  }

  &.primary {
    &::before {
      content: '';
      position: absolute;
      inset: 0;
      border: 1px solid rgba(255, 255, 255, 0.3);
      border-radius: 12px;
      pointer-events: none;
      transition: all 0.2s ease;
    }

    &::after {
      content: '';
      position: absolute;
      left: 8px;
      right: 8px;
      top: 5px;
      bottom: 5px;
      background: #161C2D;
      box-shadow: 0px 4px 10px rgba(0, 0, 0, 0.35);
      border-radius: 8px;
      z-index: -1;
    }

    &:hover {
      transform: translateY(-1px);
      color: white;
      &::before {
        border-color: rgba(255, 255, 255, 0.8);
        box-shadow: 0 0 15px rgba(255, 255, 255, 0.2);
      }
    }

    &:active {
      transform: translateY(0);
    }
  }

  &.secondary {
    position: relative;
    color: white;
    background: transparent;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    font-size: 16px;
    
    &::before {
      content: '';
      position: absolute;
      inset: 0;
      border: 1px solid rgba(255, 255, 255, 0.25);
      border-radius: 12px;
      pointer-events: none;
      opacity: 1;
      transition: all 0.2s ease;
    }
    
    &:hover {
      color: white;
      transform: translateY(-1px);
      text-decoration: none;
      
      &::before {
        opacity: 1;
        border-color: rgba(255, 255, 255, 0.8);
        box-shadow: 0 0 15px rgba(255, 255, 255, 0.2);
      }

      &::after {
        transform: translateX(4px);
      }
    }

    &::after {
      content: '→';
      transition: transform 0.2s ease;
    }

    @media (max-width: 640px) {
      font-size: 15px;
    }
  }
`;

const BetaBadge = styled.div`
  color: rgba(255, 255, 255, 0.9);
  font-size: 14px;
  margin-bottom: 6px;
  padding: 6px 16px;
  border-radius: 100px;
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(5px);
  display: inline-block;
`;

const Title = styled(motion.h1)`
  font-size: 120px;
  font-weight: 800;
  color: white;
  line-height: 1;
  letter-spacing: -0.02em;
  text-align: center;
  margin: 0;
  padding: 0;
  margin-bottom: 8px;
  width: 100%;

  @media (max-width: 1600px) {
    font-size: clamp(40px, 8vw, 120px);
  }

  @media (max-width: 768px) {
    font-size: clamp(36px, 8vw, 48px);
    letter-spacing: -0.03em;
    line-height: 1.1;
    padding: 0;
    margin-bottom: 16px;
  }
`;

const Subtitle = styled(motion.p)`
  font-size: 18px;
  color: rgba(255, 255, 255, 0.9);
  max-width: 900px;
  margin: 0;
  line-height: 1.5;
  text-align: center;
  white-space: nowrap;

  @media (max-width: 1200px) {
    white-space: normal;
    max-width: 100%;
    padding: 0;
    font-size: 16px;
  }

  @media (max-width: 768px) {
    font-size: 15px;
    line-height: 1.4;
    padding: 0 8px;
  }
`;

const AppPreview = styled(motion.img)`
  width: 100%;
  max-width: 1200px;
  height: auto;
  border-radius: 20px;
  margin-top: 12px;
  margin-bottom: 0;
  box-shadow: 
    22px 29px 48.3px 42px rgba(58, 183, 255, 0.63),
    0 20px 40px rgba(0, 0, 0, 0.2);
  object-fit: contain;
  transform: scale(1.1);

  @media (max-width: 768px) {
    margin-top: 24px;
    transform: scale(1);
    border-radius: 12px;
    box-shadow: 
      0 8px 24px rgba(58, 183, 255, 0.4),
      0 4px 8px rgba(0, 0, 0, 0.1);
    width: calc(100% - 16px);
    margin-left: 8px;
    margin-right: 8px;
  }
`;

const TrustIndicators = styled.section`
  display: flex;
  justify-content: center;
  gap: 48px;
  padding: 0 20px 48px;
  background: #0A1628;
  transform: translateY(-80%);
  margin-top: -40px;

  @media (max-width: 640px) {
    flex-direction: column;
    align-items: center;
    gap: 24px;
    padding: 16px 20px 32px;
    transform: translateY(0);
    margin-top: 0;
  }
`;

const TrustItem = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  color: rgba(255, 255, 255, 0.9);
  font-size: 16px;
  font-weight: 500;

  svg {
    width: 20px;
    height: 20px;
  color: #3AB7FF;
  }

  @media (max-width: 640px) {
    font-size: 14px;
  }
`;

const DashboardPreview = styled.div`
  width: 100%;
  max-width: 1000px;
  margin: 0 auto;
  background: #0D1117;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 
    0 0 0 1px rgba(255, 255, 255, 0.1),
    0 8px 40px rgba(0, 0, 0, 0.4);
`;

const DashboardHeader = styled.div`
  padding: 1.5rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
`;

const DashboardTitle = styled.h2`
  font-size: 1.5rem;
  color: white;
  margin: 0;
`;

const DashboardDate = styled.p`
  color: #94A3B8;
  margin: 0.5rem 0 0;
  font-size: 0.9rem;
`;

const DashboardContent = styled.div`
  padding: 2rem;
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 2rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const ProgressSection = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border-radius: 8px;
  padding: 1.5rem;
`;

const ProgressTitle = styled.h3`
  font-size: 1.1rem;
  color: white;
  margin: 0 0 1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const ProgressBar = styled.div`
  height: 6px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 3px;
  overflow: hidden;
  margin: 1rem 0;

  .fill {
    height: 100%;
    background: #2B8FFF;
    width: 45%;
    border-radius: 3px;
  }
`;

const UpcomingSection = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border-radius: 8px;
  padding: 1.5rem;
`;

const NoEvents = styled.p`
  color: #94A3B8;
  text-align: center;
  margin: 2rem 0;
`;

const ViewSchedule = styled(Link)`
  color: #2B8FFF;
  text-decoration: none;
  font-size: 0.9rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-top: 1rem;

  &:hover {
    text-decoration: underline;
  }
`;

const QuickActions = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
  margin-top: 2rem;
`;

const ActionCard = styled(Link)`
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  padding: 1.5rem;
  color: white;
  text-decoration: none;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(43, 143, 255, 0.1);
    border-color: #2B8FFF;
    transform: translateY(-2px);
  }

  h4 {
    margin: 0 0 0.5rem;
    font-size: 1.1rem;
  }

  p {
    margin: 0;
    color: #94A3B8;
    font-size: 0.9rem;
  }
`;

const TabSection = styled.div`
  position: relative;
  margin-top: 4rem;
  padding: 1rem;
  border-radius: 24px;
  background: linear-gradient(180deg, rgba(99, 102, 241, 0.05) 0%, rgba(13, 17, 23, 0) 100%);
  overflow: hidden;

  @media (max-width: 768px) {
    margin-top: 2rem;
    padding: 0.5rem;
  }

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: 24px;
    padding: 2px;
    background: linear-gradient(180deg, rgba(255, 255, 255, 0.1), transparent);
    -webkit-mask: 
      linear-gradient(#fff 0 0) content-box, 
      linear-gradient(#fff 0 0);
    -webkit-mask-composite: xor;
    mask-composite: exclude;
    pointer-events: none;
  }
`;

const TabContainer = styled.div`
  background: #1c1c1c;
  border-radius: 24px;
  padding: 1.5rem;
  width: 100%;
  max-width: 900px;
  margin: 2rem auto 0;
  box-shadow: 0 0 80px rgba(58, 183, 255, 0.15);
  border: 1px solid rgba(255, 255, 255, 0.1);
  position: relative;
  z-index: 1;

  @media (max-width: 768px) {
    padding: 1rem;
    margin: 1rem auto 0;
  }
`;

const TabHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 0.5rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  margin-bottom: 1rem;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
  
  &::-webkit-scrollbar {
    display: none;
  }

  @media (max-width: 768px) {
    padding: 0.25rem;
    gap: 0.5rem;
  }
`;

const TabButton = styled.button<{ active: boolean }>`
  background: ${props => props.active ? 'rgba(58, 183, 255, 0.1)' : 'transparent'};
  border: none;
  color: ${props => props.active ? '#3AB7FF' : '#8b949e'};
  padding: 0.75rem 1rem;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
  font-size: 0.9rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  @media (max-width: 768px) {
    padding: 0.5rem 0.75rem;
    font-size: 0.8rem;
    
    .tab-text {
      display: none;
    }
    
    .icon {
      margin: 0;
    }
  }

  &:hover {
    background: rgba(58, 183, 255, 0.05);
    color: #3AB7FF;
  }
`;

const TaskBoard = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1rem;
  padding: 0.5rem;
`;

const TaskColumn = styled.div`
  background: rgba(255, 255, 255, 0.03);
  border-radius: 12px;
  padding: 1rem;

  .column-header {
    color: #8b949e;
    font-size: 0.9rem;
    margin-bottom: 1rem;
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }
`;

const Task = styled.div`
  background: #2d333b;
  border-radius: 8px;
  padding: 1rem;
  margin-bottom: 0.5rem;
  border: 1px solid rgba(255, 255, 255, 0.1);
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    transform: translateY(-2px);
    background: #363c44;
  }

  .task-title {
    color: #e6edf3;
    font-size: 0.9rem;
    margin-bottom: 0.5rem;
  }

  .task-meta {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    color: #8b949e;
    font-size: 0.8rem;
  }
`;

const CTASection = styled.section`
  background: #0d1117;
  padding: 6rem 2rem;
  color: white;
  text-align: center;
`;

const SignupForm = styled.div`
  display: flex;
  justify-content: center;
  max-width: 500px;
  margin: 3rem auto;
`;

const SignupButton = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: #3AB8FF;
  color: white;
  padding: 1rem 2rem;
  border-radius: 8px;
  text-decoration: none;
  font-weight: 500;
  transition: all 0.2s;

  &:hover {
    transform: translateY(-2px);
    background: #50c1ff;
  }
`;

const WorkflowContainer = styled.div`
  background: #1c1c1c;
  border-radius: 16px;
  padding: 1.5rem;
  width: 900px;
  margin: 0 auto;
  box-shadow: 0 0 80px rgba(58, 183, 255, 0.15);
  border: 1px solid rgba(255, 255, 255, 0.1);
  position: relative;
  z-index: 1;
`;

const TaskGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1rem;
  padding: 0.5rem;
`;

const TaskCard = styled.div`
  background: #2d333b;
  border-radius: 8px;
  padding: 1rem;
  margin-bottom: 0.5rem;
  border: 1px solid rgba(255, 255, 255, 0.1);
  cursor: pointer;
  transition: all 0.2s ease;

  @media (max-width: 768px) {
    padding: 0.75rem;
  }

  .task-title {
    color: #e6edf3;
    font-size: 0.9rem;
    margin-bottom: 0.5rem;

    @media (max-width: 768px) {
      font-size: 0.8rem;
    }
  }

  .task-meta {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    color: #8b949e;
    font-size: 0.8rem;

    @media (max-width: 768px) {
      font-size: 0.7rem;
    }
  }
`;

const FeaturesSection = styled.section`
  padding: 6rem 2rem;
  background: #0d1117;
  color: white;
`;

const SectionTitle = styled.h2`
  text-align: center;
  font-size: clamp(2rem, 5vw, 2.5rem);
  color: white;
  margin-bottom: 3rem;

  @media (max-width: 768px) {
    margin-bottom: 2rem;
    padding: 0 1rem;
  }
`;

const FeatureContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;

  @media (max-width: 768px) {
    padding: 0 1rem;
  }
`;

const FeatureGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
  margin-top: 2rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1.5rem;
    padding: 0 1rem;
    margin-top: 1.5rem;
  }
`;

const VideoPreview = styled.div`
  aspect-ratio: 16/9;
  background: #1c1c1c;
  border-radius: 12px;
  margin-bottom: 2rem;
  overflow: hidden;
  position: relative;
  transition: all 0.3s ease;

  video {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const FeatureCard = styled(motion.div)`
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 16px;
  padding: 2.5rem;
  height: 100%;
  position: relative;
  overflow: hidden;
  backdrop-filter: blur(10px);
  transition: all 0.3s ease;

  @media (max-width: 768px) {
    padding: 1.5rem;
    margin-bottom: 1rem;
    height: auto;
    
    h3 {
      font-size: 1.3rem;
    }
    
    p {
      font-size: 1rem;
      margin-bottom: 1.5rem;
    }
    
    .benefits {
      margin-bottom: 1.5rem;
      
      li {
        font-size: 0.9rem;
      }
    }
  }

  &:hover {
    transform: translateY(-5px);
    background: rgba(255, 255, 255, 0.05);
    border-color: rgba(58, 183, 255, 0.3);
    
    .icon {
      transform: scale(1.1);
    }
  }

  .icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 56px;
    height: 56px;
    border-radius: 12px;
    background: rgba(58, 183, 255, 0.1);
    color: #3AB7FF;
    margin-bottom: 1.5rem;
    transition: all 0.3s ease;
    
    svg {
      width: 32px;
      height: 32px;
    }
  }

  h3 {
    font-size: 1.5rem;
    color: white;
    margin-bottom: 1rem;
    font-weight: 600;
  }

  p {
    color: #8b949e;
    font-size: 1.1rem;
    line-height: 1.6;
    margin-bottom: 2rem;
  }

  .benefits {
    list-style: none;
    padding: 0;
    margin: 0 0 2rem 0;

    li {
      display: flex;
      align-items: center;
      gap: 0.75rem;
      color: #8b949e;
      margin-bottom: 0.75rem;
      font-size: 1rem;

      .check {
        color: #3AB7FF;
      }
    }
  }
`;

const LearnMoreButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  color: #28c0ef;
  font-size: 0.95rem;
  text-decoration: none;
  transition: all 0.2s;
  margin-top: auto;

  &:hover {
    gap: 0.75rem;
    color: white;
  }

  @media (max-width: 768px) {
    font-size: 0.9rem;
  }
`;

const AccentGlow = styled.div`
  position: absolute;
  top: 80px;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 120px;
  height: 120px;
  background: radial-gradient(circle, rgba(58, 183, 255, 0.2) 0%, transparent 70%);
  border-radius: 50%;
  opacity: 0.5;
  transition: all 0.3s ease;
`;

const SparkleContainer = styled.div`
  position: absolute;
  inset: 0;
  overflow: hidden;
  pointer-events: none;
`;

const Sparkle = styled(motion.div)`
  position: absolute;
  width: 3px;
  height: 3px;
  background: rgba(58, 183, 255, 0.5);
  border-radius: 50%;
`;

const TabSectionWrapper = styled.div`
  position: relative;
  width: 100%;
  opacity: 1 !important;
`;

const ContentWrapper = styled(motion.div)`
  width: 100%;
`;

const SignupModal = styled.div<{ isOpen: boolean }>`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.8);
  display: ${props => props.isOpen ? 'flex' : 'none'};
  justify-content: center;
  align-items: center;
  z-index: 1000;
  padding: 2rem;
`;

const ModalContent = styled.div`
  width: 100%;
  max-width: 600px;
  height: 80vh;
  background: white;
  border-radius: 12px;
  overflow: hidden;

  iframe {
    width: 100%;
    height: 100%;
    border: none;
  }
`;

const CTABanner = styled.section`
  padding: 6rem 2rem;
  background: #0d1117;
  position: relative;
  overflow: hidden;
  text-align: center;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: radial-gradient(circle at center, rgba(58, 183, 255, 0.05) 0%, transparent 70%);
    pointer-events: none;
  }
`;

const CTAContent = styled.div`
  max-width: 900px;
  margin: 0 auto;
  background: rgba(22, 27, 34, 0.95);
  border: 1px solid rgba(58, 183, 255, 0.2);
  border-radius: 24px;
  padding: 4rem;
  text-align: center;
  position: relative;
  backdrop-filter: blur(10px);
  box-shadow: 
    0 0 80px rgba(58, 183, 255, 0.1),
    inset 0 0 0 1px rgba(255, 255, 255, 0.05);

  h2 {
    font-size: 3.5rem;
    font-weight: 800;
    margin-bottom: 1.5rem;
    background: linear-gradient(90deg, #fff, #b3b3b3);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;

    @media (max-width: 768px) {
      font-size: 2.5rem;
    }
  }

  p {
    color: #8b949e;
    font-size: 1.2rem;
    margin-bottom: 2.5rem;
    max-width: 600px;
    margin-left: auto;
    margin-right: auto;
    line-height: 1.6;
    
    @media (max-width: 768px) {
      font-size: 1rem;
    }
  }
`;

const BackgroundEffect = styled(motion.div)`
  position: absolute;
  inset: 0;
  background: radial-gradient(circle at 50% 50%, rgba(58, 183, 255, 0.1), transparent 70%);
  pointer-events: none;
  z-index: 1;
`;

const TrustBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 2rem;
  margin-top: 2rem;
  color: rgba(255, 255, 255, 0.8);
  font-size: 0.9rem;

  .trust-item {
    display: flex;
    align-items: center;
    gap: 0.5rem;

    .icon {
      color: #3AB7FF;
    }
  }

  @media (max-width: 768px) {
    flex-wrap: wrap;
    gap: 1rem;
  }
`;

const ProblemSection = styled.section`
  padding: 6rem 2rem;
  background: #0d1117;
  color: white;
  position: relative;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: radial-gradient(circle at center, rgba(255, 59, 48, 0.05) 0%, transparent 70%);
    pointer-events: none;
  }
`;

const ProblemGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
  max-width: 1200px;
  margin: 3rem auto 0;
`;

const ProblemCard = styled(motion.div)`
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 16px;
  padding: 2rem;
  
  .icon {
    color: #ff3b30;
    margin-bottom: 1rem;
  }

  h3 {
    font-size: 1.2rem;
    margin-bottom: 1rem;
    color: white;
  }

  p {
    color: #8b949e;
    line-height: 1.6;
  }
`;

const OutcomesSection = styled.section`
  padding: 6rem 2rem;
  background: #0d1117;
  position: relative;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: radial-gradient(circle at center, rgba(58, 183, 255, 0.05) 0%, transparent 70%);
    pointer-events: none;
  }
`;

const OutcomeCard = styled(motion.div)`
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 16px;
  padding: 2rem;
  text-align: start;

  .outcome-header {
    display: flex;
    align-items: center;
    gap: 1rem;
    margin-bottom: 1.5rem;

    .icon {
      color: #3AB7FF;
      font-size: 1.5rem;
    }

    h3 {
      font-size: 1.4rem;
      color: white;
      margin: 0;
    }
  }

  .description {
    color: #8b949e;
    line-height: 1.6;
    margin-bottom: 1.5rem;
  }

  .benefits {
    list-style: none;
    padding: 0;
    margin: 0;

    li {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      color: white;
      margin-bottom: 0.5rem;

      .check {
        color: #3AB7FF;
      }
    }
  }
`;

interface Task {
  title: string;
  status: string;
  meta: string;
  icon: React.ReactElement;
}

interface ViewContent {
  title: string;
  tasks: Task[];
}

interface ViewContentMap {
  teaching: ViewContent;
  learning: ViewContent;
  operations: ViewContent;
  assessment: ViewContent;
  communication: ViewContent;
}

const ComparisonSection = styled.section`
  padding: 6rem 2rem;
  background: #0d1117;
  color: white;

  @media (max-width: 768px) {
    padding: 4rem 1rem;
  }
`;

const FAQSection = styled.section`
  padding: 6rem 2rem;
  background: #0d1117;
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: radial-gradient(circle at center, rgba(58, 183, 255, 0.05) 0%, transparent 70%);
    pointer-events: none;
  }

  @media (max-width: 768px) {
    padding: 4rem 1rem;
  }
`;

const FAQGrid = styled.div`
  display: grid;
  gap: 1.5rem;
  max-width: 900px;
  margin: 0 auto;
`;

const FAQItem = styled(motion.div)`
  background: rgba(13, 17, 23, 0.75);
  backdrop-filter: blur(24px);
  -webkit-backdrop-filter: blur(24px);
  border-radius: 24px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  overflow: hidden;
`;

interface FAQHeaderProps {
  isOpen?: boolean;
}

const FAQHeader = styled.button<FAQHeaderProps>`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem 2rem;
  background: none;
  border: none;
  color: white;
  font-size: 1.1rem;
  font-weight: 600;
  text-align: start;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    color: #3AB7FF;
  }

  .icon {
    color: #3AB7FF;
    transition: transform 0.3s ease;
    transform: ${({ isOpen }) => isOpen ? 'rotate(180deg)' : 'rotate(0deg)'};
  }
`;

const FAQContent = styled(motion.div)`
  padding: 0 2rem 1.5rem;
  color: #8b949e;
  line-height: 1.6;
`;

const FeatureShowcase = styled.section`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 80px 20px;
  background: #0A1628;
  text-align: center;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: radial-gradient(circle at center, rgba(58, 183, 255, 0.08) 0%, transparent 70%);
    pointer-events: none;
  }
`;

const ImpactTitle = styled(motion.h2)`
  font-size: clamp(2.5rem, 5vw, 3.5rem);
  font-weight: 800;
  text-align: ${props => props.className === 'spark-powers' ? 'left' : 'center'};
  margin-bottom: 1.5rem;
  background: linear-gradient(135deg, #FFFFFF 0%, #3AB7FF 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  line-height: 1.2;
  max-width: 1200px;
  width: 100%;
  padding: 0 32px;
  margin-left: auto;
  margin-right: auto;

  @media (max-width: 768px) {
    font-size: clamp(2rem, 5vw, 2.5rem);
    padding: 0 20px;
  }
`;

const FeatureSubtitle = styled.p`
  font-size: 18px;
  color: rgba(255, 255, 255, 0.9);
  max-width: 1200px;
  width: 100%;
  margin: 0 auto 48px;
  line-height: 1.6;
  text-align: ${props => props.className === 'spark-powers' ? 'left' : 'center'};
  padding: 0 32px;

  @media (max-width: 768px) {
    font-size: 16px;
    padding: 0 20px;
    margin: 0 auto 32px;
  }
`;

const VideoWrapper = styled.div`
  position: absolute;
  z-index: 1;
  inset: 0;
  display: flex;
  justify-content: center;
  align-items: flex-end;
  overflow: hidden;
`;

const StyledVideo = styled.video`
  position: absolute;
  bottom: 0;
  left: 50%;
  transform: translateX(-50%);
  z-index: 10;
  width: 75%;
  height: 75%;
  border-radius: 8px 8px 0 0;
  object-fit: contain;

  @media (max-width: 768px) {
    width: 100%;
    height: auto;
    position: relative;
    border-radius: 8px;
  }
`;

const GradientContainer = styled.div`
  width: 100%;
  max-width: 1296px;
  aspect-ratio: 1296/670;
  background: linear-gradient(135deg, #1E2942 0%, #0A1628 100%);
  border-radius: 24px;
  position: relative;
  overflow: hidden;
  margin: 0 auto;
  box-shadow: 
    0 0 80px -12px rgba(58, 184, 254, 0.5),
    inset 0 2px 60px -12px rgba(58, 184, 254, 0.35);
  border: 1px solid rgba(58, 184, 254, 0.2);

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: 
      linear-gradient(90deg, 
        rgba(58, 184, 254, 0.4) -10%, 
        rgba(58, 184, 254, 0.15) 20%, 
        transparent 60%
      ),
      linear-gradient(180deg, 
        rgba(58, 184, 254, 0.35) 0%, 
        rgba(10, 22, 40, 0.15) 100%
      ),
      radial-gradient(
        circle at 70% 30%,
        rgba(58, 184, 254, 0.2) 0%,
        transparent 50%
      ),
      url('/images/background.png');
    background-size: cover;
    background-position: center;
    opacity: 0.7;
    filter: blur(8px);
    transform: scale(1.7);
    transform-origin: 40% 63%;
  }

  &::after {
    content: '';
    position: absolute;
    inset: -2px;
    background: 
      radial-gradient(
        circle at 0% 0%,
        rgba(58, 184, 254, 0.5) 0%,
        transparent 50%
      ),
      radial-gradient(
        circle at 100% 0%,
        rgba(58, 184, 254, 0.4) 0%,
        transparent 50%
      ),
      radial-gradient(
        circle at 50% 50%,
        rgba(58, 184, 254, 0.2) 0%,
        transparent 70%
      );
    mix-blend-mode: screen;
    opacity: 0.95;
    pointer-events: none;
  }

  @media (max-width: 768px) {
    border-radius: 16px;
    aspect-ratio: auto;
    height: auto;
    padding-top: 56.25%; /* 16:9 aspect ratio */
  }
`;

const GradingPreview = styled.video`
  width: 100%;
  max-width: 1200px;
  height: auto;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.15);
`;

const GradingMetrics = styled(motion.div)`
  position: absolute;
  display: flex;
  gap: 24px;
  color: white;
  z-index: 1;
`;

const MetricItem = styled(motion.div)`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  
  .value {
    font-size: 32px;
    font-weight: 700;
    color: #3AB7FF;
  }
  
  .label {
    font-size: 14px;
    color: rgba(255, 255, 255, 0.8);
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }
`;

const FeatureList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 24px;
  width: 100%;
  max-width: 700px;
  align-items: flex-start;
  padding: 0;
  margin-left: auto;
  margin-right: auto;
  transform: translateX(48px);

  @media (max-width: 768px) {
    transform: none;
    padding: 0 16px;
    max-width: 100%;
  }
`;

const FeatureItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  color: rgba(255, 255, 255, 0.9);
  font-size: 16px;
  line-height: 1.4;
  padding: 4px 0;
  transition: all 0.3s ease;
  width: 100%;
  text-align: start;

  svg {
    color: #3AB7FF;
    width: 20px;
    height: 20px;
    flex-shrink: 0;
    margin-top: 2px;
  }

  @media (max-width: 768px) {
    font-size: 14px;
  }
`;

const RoadmapSectionWrapper = styled.section`
  padding: 36px 16px;
  background: #0A1628;
  color: white;
  position: relative;
  overflow: hidden;

  @media (min-width: 1024px) {
    padding: 80px 32px 128px;
  }

  @media (min-width: 1536px) {
    padding-bottom: 240px;
  }
`;

const RoadmapContainerWrapper = styled.div`
  max-width: 1300px;
  margin: 0 auto;
  width: 100%;
  padding: 0 16px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 36px;

  @media (min-width: 1024px) {
    flex-direction: row;
    gap: 0;
    align-items: flex-start;
    overflow: visible;
  }
`;

const RoadmapContent = styled.div`
  flex: 4;
  position: relative;
  z-index: 10;
`;

const RoadmapTitle = styled.h5`
  font-size: 18px;
  line-height: 1.2;
  margin-bottom: 12px;
  font-weight: 500;

  @media (min-width: 1024px) {
    font-size: 24px;
  }
`;

const RoadmapSubtitle = styled.div`
  font-size: 14px;
  color: rgba(255, 255, 255, 0.7);
  line-height: normal;
  margin-bottom: 24px;

  @media (min-width: 1024px) {
    font-size: 18px;
  }
`;

const RoadmapCTA = styled.div`
  font-size: 12px;
  line-height: normal;
  color: rgba(255, 255, 255, 0.7);
  max-width: 202px;
  display: none;

  @media (min-width: 768px) {
    display: block;
  }

  a {
    color: white;
    text-decoration: underline;
    white-space: nowrap;
    transition: color 0.2s;

    &:hover {
      color: rgba(255, 255, 255, 0.8);
    }
  }
`;

const RoadmapImageWrapper = styled.div`
  flex: 10;
  position: relative;
  transform: scale(1.1);
  min-width: 100vw;

  @media (min-width: 768px) {
    transform: scale(1);
  }

  @media (min-width: 1024px) {
    min-width: 0;
    transform: translateX(24px);
  }

  @media (min-width: 1536px) {
    transform: translateX(72px) translateY(112px) scale(1.2);
    max-width: max-content;
  }

  &::after {
    @media (min-width: 1280px) {
      content: '';
      width: 33.333333%;
      background: linear-gradient(to left, #0A1628, transparent);
      position: absolute;
      top: 0;
      right: 0;
      height: 100%;
    }
  }
`;

const RoadmapSVG = styled.img`
  width: 100%;
  height: auto;
  user-select: none;
`;

const MobileRoadmapCTA = styled.div`
  font-size: 12px;
  line-height: normal;
  color: rgba(255, 255, 255, 0.7);
  max-width: 202px;
  align-self: flex-start;

  @media (min-width: 1024px) {
    display: none;
  }

  a {
    color: white;
    text-decoration: underline;
    white-space: nowrap;
    transition: color 0.2s;

    &:hover {
      color: rgba(255, 255, 255, 0.8);
    }
  }
`;

const AnimatedVector = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 280px;
  display: flex;
  justify-content: center;
  align-items: flex-end;
  padding-bottom: 32px;

  svg {
    width: 240px;
    height: 240px;
    filter: drop-shadow(0 8px 24px rgba(58, 183, 255, 0.3));
    transition: all 0.8s cubic-bezier(0.4, 0, 0.2, 1);
    animation: float 3s ease-in-out infinite;

    @keyframes float {
      0%, 100% {
        transform: translateY(0);
      }
      50% {
        transform: translateY(-15px);
      }
    }

    &:hover {
      transform: translateY(-12px) scale(1.1);
      filter: drop-shadow(0 24px 48px rgba(58, 183, 255, 0.5));

      path {
        transition: all 0.6s ease;
        stroke-dasharray: 1000;
        stroke-dashoffset: 1000;
        animation: draw 1.5s ease forwards;
      }

      circle {
        animation: pulse 2s ease-in-out infinite;
      }
    }

    @keyframes draw {
      to {
        stroke-dashoffset: 0;
      }
    }

    @keyframes pulse {
      0%, 100% {
        opacity: 0.8;
        transform: scale(1);
      }
      50% {
        opacity: 0.4;
        transform: scale(1.1);
      }
    }
  }
`;

const ImpactLabel = styled.div`
  font-size: 32px;
  font-weight: 700;
  color: white;
  margin-bottom: 24px;
  text-align: start;
`;

const ImpactDescription = styled.div`
  font-size: 20px;
  color: rgba(255, 255, 255, 0.7);
  line-height: 1.6;
  margin-bottom: 280px;
  text-align: start;
  max-width: 90%;
`;

const CardImage = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 240px;
  margin-top: auto;
  background-size: contain;
  background-position: center bottom;
  background-repeat: no-repeat;

  &.frontier-image {
    background-image: url('/images/frontier-intelligence.png');
  }

  &.familiar-image {
    background-image: url('/images/feels-familiar.png');
  }

  &.privacy-image {
    background-image: url('/images/privacy-options.png');
  }
`;

const SeeMoreButton = styled.button`
  background: white;
  color: black;
  border: none;
  border-radius: 8px;
  padding: 12px 24px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  margin-top: 40px;
  transition: all 0.2s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }
`;

const ImpactShowcase = styled.section`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 80px 20px;
  background: #0A1628;
  text-align: center;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: radial-gradient(circle at center, rgba(58, 183, 255, 0.08) 0%, transparent 70%);
    pointer-events: none;
  }
`;

const ImpactGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 24px;
  max-width: 1200px;
  width: 100%;
  margin: 48px auto;
  padding: 0 20px;

  @media (max-width: 1024px) {
    grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
    gap: 20px;
  }
`;

const ImpactCard = styled.div`
  background: rgba(28, 32, 40, 0.6);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 24px;
  padding: 32px;
  min-height: 480px;
  position: relative;
  transition: all 0.3s ease;
  display: flex;
  flex-direction: column;
  overflow: visible;
  text-align: start;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(
      120deg,
      rgba(58, 183, 255, 0.05) 0%,
      rgba(58, 183, 255, 0.02) 100%
    );
    pointer-events: none;
    z-index: 1;
    border-radius: 24px;
  }

  &:hover {
    border-color: rgba(58, 183, 255, 0.4);
    box-shadow:
      0 8px 32px rgba(58, 183, 255, 0.15),
      inset 0 0 32px rgba(58, 183, 255, 0.05);
    transform: translateY(-2px);
  }
`;

const GoalSection = styled.section`
  padding: 48px 24px;
  background: #0A1628;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: radial-gradient(circle at center, rgba(58, 183, 255, 0.05) 0%, transparent 70%);
    pointer-events: none;
  }
`;

const GoalContainer = styled.div`
  max-width: 996px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 20px;
  width: 69.167vw;

  @media (max-width: 640px) {
    width: 100%;
  }
`;

const GoalText = styled.div`
  font-size: clamp(32px, 3.75vw, 56px);
  font-weight: 300;
  line-height: 1.2;
  letter-spacing: -2.24px;
  margin-bottom: 32px;
  text-wrap: pretty;

  p {
    margin-bottom: 5px;

    &:first-child {
      color: #909090;
    }

    &:last-child {
      color: white;
    }

    .highlight {
      color: #3AB7FF;
    }
  }
`;

const MissionText = styled.p`
  font-size: clamp(32px, 3.75vw, 56px);
  font-weight: 300;
  line-height: 1.2;
  letter-spacing: -2.24px;
  text-wrap: pretty;

  .highlight {
    color: #3AB7FF;
  }
`;

const LearnMoreLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: #3AB7FF;
  font-size: 16px;
  font-weight: 400;
  text-decoration: none;
  transition: all 0.2s ease;
  margin-top: 8px;

  svg {
    transition: transform 0.2s ease;
  }

  &:hover {
    color: white;

    svg {
      transform: translateX(4px);
    }
  }
`;

const BetaSignupCard = styled(Link)`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  padding: 64px 32px;
  margin: 27px auto;
  width: 85%;
  max-width: 1101px;
  min-height: 320px;
  background: linear-gradient(135deg, rgba(58, 183, 255, 0.1) 0%, rgba(10, 22, 40, 0.1) 100%);
  backdrop-filter: blur(12px);
  border-radius: 20px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  overflow: hidden;
  transition: all 0.3s ease;
  text-decoration: none;

  &::before {
    content: '';
    position: absolute;
    top: -50px;
    right: -50px;
    width: 200px;
    height: 200px;
    background: linear-gradient(135deg, rgba(58, 183, 255, 0.2) 0%, rgba(58, 183, 255, 0.05) 100%);
    border-radius: 20px;
    transform: rotate(45deg);
    z-index: 1;
  }

  &::after {
    content: '';
    position: absolute;
    bottom: -30px;
    left: -30px;
    width: 150px;
    height: 150px;
    background: linear-gradient(225deg, rgba(58, 183, 255, 0.15) 0%, rgba(58, 183, 255, 0.03) 100%);
    border-radius: 15px;
    transform: rotate(-30deg);
    z-index: 1;
  }

  &:hover {
    border-color: rgba(58, 183, 255, 0.4);
    transform: translateY(-2px);
  }

  @media (max-width: 768px) {
    padding: 32px;
    margin: 14px;
    min-height: 300px;
  }
`;

const BetaContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  z-index: 2;
  max-width: 600px;
  position: relative;
`;

const BetaTitle = styled.h2`
  font-size: 56px;
  font-weight: 800;
  color: white;
  margin: 0;
  line-height: 1.1;
  text-align: start;

  @media (max-width: 768px) {
    font-size: 36px;
  }
`;

const BetaDescription = styled.p`
  font-size: 20px;
  color: rgba(255, 255, 255, 0.7);
  line-height: 1.6;
  margin: 0;
  text-align: start;

  .highlight {
    color: #3AB7FF;
    font-weight: 600;
  }

  @media (max-width: 768px) {
    font-size: 16px;
  }
`;

const BetaCTA = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 15px;
  font-weight: 600;
  color: #000;
  margin-top: 8px;
  background: #3AB7FF;
  padding: 10px 20px;
  border-radius: 8px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(58, 183, 255, 0.25);
  width: fit-content;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      120deg,
      transparent 0%,
      rgba(255, 255, 255, 0.3) 50%,
      transparent 100%
    );
    transform: translateX(-100%);
    transition: transform 0.6s cubic-bezier(0.4, 0, 0.2, 1);
  }

  svg {
    transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    width: 16px;
    height: 16px;
  }

  &:hover {
    background: #4dc2ff;
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(58, 183, 255, 0.35);

    &::before {
      transform: translateX(100%);
    }

    svg {
      transform: translateX(4px);
    }
  }

  &:active {
    transform: translateY(0);
    box-shadow: 0 2px 8px rgba(58, 183, 255, 0.2);
  }

  @media (max-width: 768px) {
    padding: 8px 16px;
    font-size: 14px;
  }
`;

const LandingPage: React.FC = () => {
  const { t } = useTranslation();
  const [activeView, setActiveView] = useState<'teaching' | 'learning' | 'operations' | 'assessment' | 'communication'>('teaching');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const viewContentVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.4, ease: "easeOut" }
    },
    exit: {
      opacity: 0,
      y: -20,
      transition: { duration: 0.3 }
    }
  };

  const viewContent: ViewContentMap = {
    teaching: {
      title: "Teaching & Assessment Hub",
      tasks: [
        {
          title: "AI Teaching Assistant",
          status: "Always ready",
          meta: "Your personal AI helper for all tasks",
          icon: <Robot />
        },
        {
          title: "Content Creator",
          status: "AI powered",
          meta: "Generate lessons, worksheets & materials",
          icon: <MagicWand />
        },
        {
          title: "Grade Exams",
          status: "Processing ready",
          meta: "Grade assignments with AI assistance",
          icon: <CheckSquare />
        },
        {
          title: "Class Insights",
          status: "Live analytics",
          meta: "Track progress & identify gaps",
          icon: <ChartLineUp />
        },
        {
          title: "Smart Planner",
          status: "Weekly view",
          meta: "Optimize your teaching schedule",
          icon: <Calendar />
        },
        {
          title: "Resource Library",
          status: "1000+ templates",
          meta: "Ready-to-use educational content",
          icon: <Books />
        }
      ]
    },
    assessment: {
      title: "Assessment & Feedback Suite",
      tasks: [
        {
          title: "Question Bank",
          status: "AI-powered",
          meta: "Generate custom questions & quizzes",
          icon: <Database />
        },
        {
          title: "Smart Feedback",
          status: "Personalized",
          meta: "Auto-generate student feedback",
          icon: <ChatText />
        },
        {
          title: "Progress Reports",
          status: "Auto-generate",
          meta: "Detailed student assessments",
          icon: <NotePencil />
        },
        {
          title: "Behavior Monitor",
          status: "Real-time",
          meta: "Track engagement & participation",
          icon: <Users />
        },
        {
          title: "Performance Trends",
          status: "Analytics ready",
          meta: "Visualize learning patterns",
          icon: <ChartBar />
        },
        {
          title: "Parent Updates",
          status: "Automated",
          meta: "Keep parents informed & engaged",
          icon: <Envelope />
        }
      ]
    },
    communication: {
      title: "Communication Hub",
      tasks: [
        {
          title: "Parent Updates",
          status: "Draft ready",
          meta: "Automated progress reports",
          icon: <Envelope />
        },
        {
          title: "Translation Tool",
          status: "30+ languages",
          meta: "Break language barriers",
          icon: <Translate />
        },
        {
          title: "Office Hours",
          status: "Schedule open",
          meta: "Virtual meeting management",
          icon: <CalendarCheck />
        },
        {
          title: "Announcement System",
          status: "School-wide reach",
          meta: "Multi-channel communications",
          icon: <MegaphoneSimple />
        },
        {
          title: "Document Sharing",
          status: "Secure platform",
          meta: "Share resources safely",
          icon: <ShareNetwork />
        },
        {
          title: "Community Forum",
          status: "Active discussions",
          meta: "Collaborate with colleagues",
          icon: <Users />
        }
      ]
    },
    learning: {
      title: "Smart Learning Platform",
      tasks: [
        {
          title: "AI Study Buddy",
          status: "Always available",
          meta: "24/7 personalized tutoring & homework help",
          icon: <Robot />
        },
        {
          title: "Math Mastery",
          status: "Interactive practice",
          meta: "From algebra to calculus with step-by-step help",
          icon: <Calculator />
        },
        {
          title: "Science Lab",
          status: "Virtual experiments ready",
          meta: "Interactive simulations & explanations",
          icon: <Atom />
        },
        {
          title: "Writing Coach",
          status: "Real-time feedback",
          meta: "Essays, research papers & creative writing",
          icon: <PencilLine />
        },
        {
          title: "Language Learning",
          status: "12 languages available",
          meta: "AI-powered conversation practice",
          icon: <Translate />
        },
        {
          title: "Study Planner",
          status: "Personalized for you",
          meta: "Smart scheduling & revision reminders",
          icon: <Brain />
        }
      ]
    },
    operations: {
      title: "School Management Suite",
      tasks: [
        {
          title: "Performance Analytics",
          status: "Live dashboard",
          meta: "School-wide insights ready",
          icon: <ChartBar />
        },
        {
          title: "Resource Planning",
          status: "AI optimization active",
          meta: "Improving resource allocation",
          icon: <Gear />
        },
        {
          title: "Staff Development",
          status: "3 new recommendations",
          meta: "Personalized PD paths",
          icon: <GraduationCap />
        },
        {
          title: "Parent Engagement",
          status: "Weekly digest ready",
          meta: "Automated communications",
          icon: <Users />
        },
        {
          title: "Curriculum Mapping",
          status: "Updates available",
          meta: "AI-enhanced planning tools",
          icon: <MapTrifold />
        }
      ]
    }
  };
  const tabSectionRef = useRef(null);

  const { scrollYProgress } = useScroll({
    target: tabSectionRef,
    offset: ["start end", "center center"]
  });

  const opacity = useTransform(scrollYProgress, [0, 0.5], [1, 1]);
  const scale = useTransform(scrollYProgress, [0, 0.5], [1, 1]);

  const sparkles = Array.from({ length: 20 }).map((_, i) => ({
    id: i,
    x: Math.random() * 100,
    y: Math.random() * 100,
    scale: Math.random() * 0.5 + 0.5,
    duration: Math.random() * 2 + 1
  }));

  const backgroundY = useTransform(scrollYProgress, [0, 1], ['0%', '100%']);

  const faqs = [
    {
      question: "Can Spark integrate with our existing LMS?",
      answer: "Yes, Spark seamlessly integrates with popular Learning Management Systems including Canvas, Blackboard, and Google Classroom. Our API and SSO support make it easy to connect with your existing educational tools."
    },
    {
      question: "Do teachers need training to use Spark?",
      answer: "Spark is designed to be intuitive and user-friendly. While we provide comprehensive onboarding resources and support, most teachers can start using basic features immediately. We also offer optional training sessions for advanced features."
    },
    {
      question: "Is SparkSkool secure for teacher data?",
      answer: "Absolutely. We prioritize data security with enterprise-grade encryption, FERPA compliance, and regular security audits. All data is stored securely, and we never share sensitive information with third parties."
    },
    {
      question: "How quickly can we implement Spark in our school?",
      answer: "Implementation typically takes 1-2 weeks, including integration with existing systems and initial teacher onboarding. Our dedicated support team works with you throughout the process to ensure a smooth transition."
    },
    {
      question: "What kind of support do you provide?",
      answer: "We offer 24/7 technical support, dedicated customer success managers, and regular check-ins to ensure you're getting the most out of Spark. Our team is always available to help with any questions or concerns."
    }
  ];

  useEffect(() => {
    const tabOrder: Array<'teaching' | 'learning' | 'assessment' | 'communication' | 'operations'> = [
      'teaching',
      'learning',
      'assessment',
      'communication',
      'operations'
    ];

    const interval = setInterval(() => {
      setActiveView(currentView => {
        const currentIndex = tabOrder.indexOf(currentView);
        const nextIndex = (currentIndex + 1) % tabOrder.length;
        return tabOrder[nextIndex];
      });
    }, 10000); // 10 seconds

    // Reset interval when user manually changes tab
    const resetOnClick = () => {
      clearInterval(interval);
    };

    // Add click listeners to tab buttons
    const tabButtons = document.querySelectorAll('[role="tab"]');
    tabButtons.forEach(button => {
      button.addEventListener('click', resetOnClick);
    });

    // Cleanup
    return () => {
      clearInterval(interval);
      tabButtons.forEach(button => {
        button.removeEventListener('click', resetOnClick);
      });
    };
  }, []);

  return (
    <>
      <SEO />
      <Helmet>
        <script type="text/javascript">
          {`
            (function (C, A, L) { 
              let p = function (a, ar) { a.q.push(ar); }; 
              let d = C.document; 
              C.Cal = C.Cal || function () { 
                let cal = C.Cal; 
                let ar = arguments; 
                if (!cal.loaded) { 
                  cal.ns = {}; 
                  cal.q = cal.q || []; 
                  d.head.appendChild(d.createElement("script")).src = A; 
                  cal.loaded = true; 
                } 
                if (ar[0] === L) { 
                  const api = function () { p(api, arguments); }; 
                  const namespace = ar[1]; 
                  api.q = api.q || []; 
                  if(typeof namespace === "string"){
                    cal.ns[namespace] = cal.ns[namespace] || api;
                    p(cal.ns[namespace], ar);
                    p(cal, ["initNamespace", namespace]);
                  } else p(cal, ar); 
                  return;
                } 
                p(cal, ar); 
              }; 
            })(window, "https://app.cal.com/embed/embed.js", "init");
            Cal("init", "20-min", {origin:"https://cal.com"});
            Cal.ns["20-min"]("ui", {"hideEventTypeDetails":false,"layout":"month_view"});
          `}
        </script>
      </Helmet>
      <GlobalStyle />
      <Container>
        <HeroSection>
          <HeroContainer>
            <HeroContent>
              <Title
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
              >
                {t('The AI Co-Teacher')}
              </Title>
              <Subtitle
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
              >
                {t("Built to make teaching more powerful at every level, Spark is the best way to bring AI into your school.")}
              </Subtitle>
              <ButtonGroup>
                <Button to="https://app.sparkskool.com" className="primary">
                  {t("Start for free")}
                </Button>
                <Button
                  to="#"
                  className="secondary"
                  data-cal-link="sparkskool/20-min"
                  data-cal-namespace="20-min"
                  data-cal-config='{"layout":"month_view"}'
                >
                  {t("Book a demo")}
                </Button>
              </ButtonGroup>

              <AppPreview
                src="/images/spark-app-preview.png"
                alt="Spark App Preview"
                initial={{ opacity: 0, y: 40 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.4 }}
              />
            </HeroContent>
          </HeroContainer>
        </HeroSection>

        <TrustIndicators>
          <TrustItem>
            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 2L3 6V12C3 16.97 6.85 21.47 12 23C17.15 21.47 21 16.97 21 12V6L12 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            {t("Trusted by 100+ Schools")}
          </TrustItem>
          <TrustItem>
            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M9 12L11 14L15 10M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            {t("FERPA Compliant")}
          </TrustItem>
          <TrustItem>
            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 8V12L15 15M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            {t("Save 15+ Hours Weekly")}
          </TrustItem>
        </TrustIndicators>

        <FeatureShowcase>
          <ImpactTitle
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            {t("Grade in minutes, not hours")}
          </ImpactTitle>
          <FeatureSubtitle>
            {t("With Spark's GradeWizard, teachers can grade up to 100 papers — including handwritten answers — in just minutes")}
          </FeatureSubtitle>
          <GradientContainer>
            <VideoWrapper>
              <StyledVideo
                autoPlay
                muted
                loop
                playsInline
                preload="auto"
              >
                <source src="https://s3.us-east-1.amazonaws.com/assets.leapthelimit.com/Spark/spark-grading-preview.mp4" type="video/mp4" />
                Your browser does not support the video tag.
              </StyledVideo>
            </VideoWrapper>
          </GradientContainer>
          <FeatureList>
            <FeatureItem>
              <CheckCircle weight="bold" />
              {t("Works with open-ended questions, handwritten examples, and even scanned papers")}
            </FeatureItem>
            <FeatureItem>
              <CheckCircle weight="bold" />
              {t("Upload your teacher answer sheet, PDFs, or text — Spark reads and analyzes both instantly")}
            </FeatureItem>
            <FeatureItem>
              <CheckCircle weight="bold" />
              {t("Approve, adjust, or edit Spark's feedback before exporting or saving grades")}
            </FeatureItem>
          </FeatureList>
        </FeatureShowcase>

        <FeatureShowcase>
          <ImpactTitle>
            {t("Teach your way, Test your way")}
          </ImpactTitle>
          <FeatureSubtitle>
            {t("Spark creates full exams based on your objectives, language, and classroom style — without copy-pasting.")}
          </FeatureSubtitle>
          <GradientContainer>
            <VideoWrapper>
              <StyledVideo
                autoPlay
                muted
                loop
                playsInline
                preload="auto"
              >
                <source src="https://assets.leapthelimit.com/Spark/Exam-Preview.mp4" type="video/mp4" />
                Your browser does not support the video tag.
              </StyledVideo>
            </VideoWrapper>
          </GradientContainer>
          <FeatureList>
            <FeatureItem>
              <CheckCircle weight="bold" />
              {t("Just type your lesson goals, topic, or skills to assess")}
            </FeatureItem>
            <FeatureItem>
              <CheckCircle weight="bold" />
              {t("It creates full-length questions in your style — multiple choice, open-ended, or custom formats")}
            </FeatureItem>
            <FeatureItem>
              <CheckCircle weight="bold" />
              {t("Edit anything, add your voice, and export. Ready to print or share")}
            </FeatureItem>
          </FeatureList>
        </FeatureShowcase>

        <ImpactShowcase>
          <ImpactTitle className="spark-powers">
            {t("Meet Your Spark Superpowers")}
          </ImpactTitle>
          <FeatureSubtitle className="spark-powers">
            {t("Spark is more than an AI — it's your co-teacher, your planner, your second brain.")}
          </FeatureSubtitle>
          <ImpactGrid>
            <ImpactCard>
              <ImpactLabel>{t("SlideMaker")}</ImpactLabel>
              <ImpactDescription>
                {t("Transform your lessons into engaging presentations. Beautiful slides with animations, diagrams, and examples in seconds.")}
              </ImpactDescription>
              <AnimatedVector>
                <svg viewBox="0 0 200 200" xmlns="http://www.w3.org/2000/svg">
                  <defs>
                    <linearGradient id="slide-grad" x1="0" y1="0" x2="100%" y2="100%">
                      <stop offset="0%" stopColor="#3AB7FF" stopOpacity="1">
                        <animate attributeName="stopOpacity" values="1;0.5;1" dur="3s" repeatCount="indefinite" />
                      </stop>
                      <stop offset="100%" stopColor="#3AB7FF" stopOpacity="0.5">
                        <animate attributeName="stopOpacity" values="0.5;0.2;0.5" dur="3s" repeatCount="indefinite" />
                      </stop>
                    </linearGradient>
                    <linearGradient id="slide-grad-2" x1="0" y1="0" x2="0" y2="100%">
                      <stop offset="0%" stopColor="#3AB7FF" stopOpacity="0.8">
                        <animate attributeName="stopOpacity" values="0.8;0.4;0.8" dur="4s" repeatCount="indefinite" />
                      </stop>
                      <stop offset="100%" stopColor="#3AB7FF" stopOpacity="0.2">
                        <animate attributeName="stopOpacity" values="0.2;0.1;0.2" dur="4s" repeatCount="indefinite" />
                      </stop>
                    </linearGradient>
                  </defs>
                  <path d="M60,140 L100,60 L140,140 Z" fill="url(#slide-grad)" stroke="#3AB7FF" strokeWidth="2">
                    <animate attributeName="d" dur="4s" repeatCount="indefinite"
                      values="M60,140 L100,60 L140,140 Z;
                              M55,145 L100,55 L145,145 Z;
                              M60,140 L100,60 L140,140 Z" />
                  </path>
                  <path d="M60,140 L100,60 L80,120 Z" fill="url(#slide-grad-2)" opacity="0.7">
                    <animate attributeName="opacity" values="0.7;0.4;0.7" dur="3s" repeatCount="indefinite" />
                  </path>
                  <path d="M140,140 L100,60 L120,120 Z" fill="url(#slide-grad-2)" opacity="0.5">
                    <animate attributeName="opacity" values="0.5;0.3;0.5" dur="3.5s" repeatCount="indefinite" />
                  </path>
                  <path d="M80,120 L100,60 L120,120 L100,140 Z" fill="white" opacity="0.2">
                    <animate attributeName="opacity" values="0.2;0.1;0.2" dur="2.5s" repeatCount="indefinite" />
                  </path>
                </svg>
              </AnimatedVector>
            </ImpactCard>
            <ImpactCard>
              <ImpactLabel>{t("GameMaster")}</ImpactLabel>
              <ImpactDescription>
                {t("Create curriculum-aligned games in one click. Bring energy into your lessons — without extra prep.")}
              </ImpactDescription>
              <AnimatedVector>
                <svg viewBox="0 0 200 200" xmlns="http://www.w3.org/2000/svg">
                  <defs>
                    <linearGradient id="game-grad" x1="0" y1="0" x2="100%" y2="100%">
                      <stop offset="0%" stopColor="#3AB7FF" stopOpacity="1">
                        <animate attributeName="stopOpacity" values="1;0.6;1" dur="3s" repeatCount="indefinite" />
                      </stop>
                      <stop offset="100%" stopColor="#3AB7FF" stopOpacity="0.5">
                        <animate attributeName="stopOpacity" values="0.5;0.2;0.5" dur="3s" repeatCount="indefinite" />
                      </stop>
                    </linearGradient>
                  </defs>
                  <path d="M100,40 L140,60 L140,100 L100,120 L60,100 L60,60 Z" fill="url(#game-grad)" stroke="#3AB7FF" strokeWidth="2">
                    <animate attributeName="d" dur="4s" repeatCount="indefinite"
                      values="M100,40 L140,60 L140,100 L100,120 L60,100 L60,60 Z;
                              M100,35 L145,55 L145,105 L100,125 L55,105 L55,55 Z;
                              M100,40 L140,60 L140,100 L100,120 L60,100 L60,60 Z" />
                  </path>
                  <path d="M100,40 L140,60 L120,80 L80,60 Z" fill="white" opacity="0.3">
                    <animate attributeName="opacity" values="0.3;0.1;0.3" dur="3s" repeatCount="indefinite" />
                  </path>
                  <path d="M60,60 L80,60 L80,100 L60,100 Z" fill="white" opacity="0.1">
                    <animate attributeName="opacity" values="0.1;0.05;0.1" dur="2.5s" repeatCount="indefinite" />
                  </path>
                  <path d="M100,120 L80,100 L80,60 L100,80 Z" fill="white" opacity="0.2">
                    <animate attributeName="opacity" values="0.2;0.1;0.2" dur="3.5s" repeatCount="indefinite" />
                  </path>
                </svg>
              </AnimatedVector>
            </ImpactCard>
            <ImpactCard>
              <ImpactLabel>{t("AssignmentMaker")}</ImpactLabel>
              <ImpactDescription>
                {t("Auto-generate differentiated classwork. Tailored tasks for mixed abilities, topics, and learning goals.")}
              </ImpactDescription>
              <AnimatedVector>
                <svg viewBox="0 0 200 200" xmlns="http://www.w3.org/2000/svg">
                  <defs>
                    <linearGradient id="assign-grad" x1="0" y1="0" x2="100%" y2="100%">
                      <stop offset="0%" stopColor="#3AB7FF" stopOpacity="1">
                        <animate attributeName="stopOpacity" values="1;0.6;1" dur="3s" repeatCount="indefinite" />
                      </stop>
                      <stop offset="100%" stopColor="#3AB7FF" stopOpacity="0.5">
                        <animate attributeName="stopOpacity" values="0.5;0.2;0.5" dur="3s" repeatCount="indefinite" />
                      </stop>
                    </linearGradient>
                    <linearGradient id="assign-grad-2" x1="100%" y1="0" x2="0" y2="100%">
                      <stop offset="0%" stopColor="#3AB7FF" stopOpacity="0.8">
                        <animate attributeName="stopOpacity" values="0.8;0.4;0.8" dur="4s" repeatCount="indefinite" />
                      </stop>
                      <stop offset="100%" stopColor="#3AB7FF" stopOpacity="0.2">
                        <animate attributeName="stopOpacity" values="0.2;0.1;0.2" dur="4s" repeatCount="indefinite" />
                      </stop>
                    </linearGradient>
                  </defs>
                  <path d="M60,120 L100,60 L140,120 L100,160 Z" fill="url(#assign-grad)" stroke="#3AB7FF" strokeWidth="2">
                    <animate attributeName="d" dur="4s" repeatCount="indefinite"
                      values="M60,120 L100,60 L140,120 L100,160 Z;
                              M55,125 L100,55 L145,125 L100,165 Z;
                              M60,120 L100,60 L140,120 L100,160 Z" />
                  </path>
                  <path d="M60,120 L100,60 L100,160 Z" fill="url(#assign-grad-2)" opacity="0.6">
                    <animate attributeName="opacity" values="0.6;0.3;0.6" dur="3s" repeatCount="indefinite" />
                  </path>
                  <path d="M100,60 L140,120 L100,160 Z" fill="white" opacity="0.2">
                    <animate attributeName="opacity" values="0.2;0.1;0.2" dur="3.5s" repeatCount="indefinite" />
                  </path>
                  <path d="M80,100 L100,80 L120,100 L100,120 Z" fill="white" opacity="0.3">
                    <animate attributeName="opacity" values="0.3;0.15;0.3" dur="2.5s" repeatCount="indefinite" />
                  </path>
                </svg>
              </AnimatedVector>
            </ImpactCard>
          </ImpactGrid>
          <SeeMoreButton>
            {t("SEE MORE FEATURES")}
          </SeeMoreButton>
        </ImpactShowcase>

        <BetaSignupCard to="/beta">
          <BetaContent>
            <BetaTitle>{t("Join Spark Beta")}</BetaTitle>
            <BetaDescription>
              {t("Get early access to ")}<span className="highlight">Spark</span>{t(" and help shape the future of education technology. Limited spots available for our beta program.")}
            </BetaDescription>
            <BetaCTA>
              {t("Join Beta Program")}
              <ArrowRight weight="bold" />
            </BetaCTA>
          </BetaContent>
        </BetaSignupCard>

        <GoalSection>
          <GoalContainer>
            <GoalText>
              <p>{t('Our goal is to break through the "teaching vs technology" divide.')}</p>
              <p>{t("We work to ")}<span className="highlight">{t("empower every educator")}</span>{t(" who's working to shape the future of education.")}</p>
            </GoalText>
            <LearnMoreLink href="/mission">
              {t("Read more about our mission")}
              <ArrowRight weight="bold" />
            </LearnMoreLink>
          </GoalContainer>
        </GoalSection>

        <RoadmapSectionWrapper>
          <RoadmapContainerWrapper>
            <RoadmapContent>
              <RoadmapTitle>{t("Our Roadmap")}</RoadmapTitle>
              <RoadmapSubtitle>
                {t("See the milestones that brought us here, and get a sneak peak of what's next.")}
              </RoadmapSubtitle>
              <RoadmapCTA>
                {t("Have some feature request? ")}
                <Link to="/beta">{t("Join our Beta")}</Link> {t("and tell us more!")}
              </RoadmapCTA>
            </RoadmapContent>

            <RoadmapImageWrapper>
              <RoadmapSVG
                src="/images/spark-roadmap.svg"
                alt="Spark Development Roadmap"
                width={912}
                height={480}
                loading="lazy"
                decoding="async"
              />
            </RoadmapImageWrapper>

            <MobileRoadmapCTA>
              {t("Have some feature request? ")}
              <Link to="/beta">{t("Join our Beta")}</Link> {t("and tell us more!")}
            </MobileRoadmapCTA>
          </RoadmapContainerWrapper>
        </RoadmapSectionWrapper>
      </Container>
    </>
  );
};

export default LandingPage;